import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { listPublishedPosts, type BlogPost } from '@/lib/blog';
import { useSeo } from '@/lib/seoHead';
import { Spinner } from '@/components/Spinner';
import { cn } from '@/lib/utils';

const SITE_URL = 'https://cnapractice.com';
const PAGE_SIZE = 9;

export function BlogIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPage = parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  // Page-aware canonical so each pagination URL has its own canonical and
  // doesn't compete for the same SERP spot.
  const canonical = page === 1 ? `${SITE_URL}/blog` : `${SITE_URL}/blog?page=${page}`;
  useSeo({
    title: page === 1
      ? 'CNA Exam Prep Guides & Articles · CNA Prep Pro'
      : `CNA Exam Prep Guides — Page ${page} · CNA Prep Pro`,
    description:
      'Study guides, exam tips, NNAAP category breakdowns, and clinical scenarios written for CNA candidates. Updated regularly.',
    canonical,
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'CNA Prep Pro Blog',
      url: `${SITE_URL}/blog`,
      description:
        'Study guides, exam tips, NNAAP category breakdowns, and clinical scenarios for CNA candidates.',
    },
  });

  const q = useQuery({
    queryKey: ['blog-published-posts'],
    queryFn: () => listPublishedPosts(),
    staleTime: 5 * 60 * 1000,
  });

  const all = q.data ?? [];
  const totalPages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return all.slice(start, start + PAGE_SIZE);
  }, [all, safePage]);

  function goTo(next: number) {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100">
      {/* Slim public top bar — separate from the learner app shell so the
          blog is fully crawlable + self-contained. */}
      <header className="border-b border-stone-200 dark:border-stone-800 bg-stone-50/90 dark:bg-stone-950/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-brand-700 dark:text-brand-400">CNA Prep Pro</Link>
          <nav className="flex items-center gap-3 sm:gap-5 text-sm">
            <Link to="/blog" className="text-stone-700 dark:text-stone-300 font-semibold">Blog</Link>
            <Link to="/" className="text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100">Home</Link>
            <Link to="/trial/new" className="inline-flex items-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 font-semibold shadow-sm">
              Try free
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-10 sm:mb-12">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-700 dark:text-brand-400">
            Articles
          </span>
          <h1 className="mt-2 text-3xl sm:text-[2.5rem] font-bold tracking-tight leading-[1.1]">
            Study guides for the CNA exam
          </h1>
          <p className="mt-3 text-stone-600 dark:text-stone-400 text-lg max-w-prose">
            NNAAP category breakdowns, clinical scenarios, and exam tips —
            written for working caregivers, immigrants, and career-changers.
          </p>
        </header>

        {q.isLoading ? (
          <div className="flex justify-center py-12"><Spinner className="h-6 w-6 text-brand-600" /></div>
        ) : q.isError ? (
          <div className="text-center py-8">
            <p className="text-rose-700 dark:text-rose-400 font-semibold mb-2">Could not load posts.</p>
            <p className="text-xs text-stone-500 max-w-prose mx-auto break-words">
              {(q.error as { message?: string }).message ?? 'unknown error'}
            </p>
          </div>
        ) : all.length === 0 ? (
          <p className="text-center text-stone-600 dark:text-stone-400 py-12">
            No posts yet — fresh content is on the way.
          </p>
        ) : (
          <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pageItems.map((p) => <PostCard key={p.slug} post={p} />)}
            </ul>

            {totalPages > 1 && (
              <Pagination
                page={safePage}
                totalPages={totalPages}
                onChange={goTo}
                rangeStart={(safePage - 1) * PAGE_SIZE + 1}
                rangeEnd={Math.min(safePage * PAGE_SIZE, all.length)}
                total={all.length}
              />
            )}
          </>
        )}
      </main>

      <footer className="border-t border-stone-200 dark:border-stone-800 py-6 mt-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-xs text-stone-500 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} CNA Prep Pro</span>
          <nav className="flex items-center gap-4">
            <Link to="/" className="hover:text-stone-700 dark:hover:text-stone-300">Home</Link>
            <Link to="/blog" className="hover:text-stone-700 dark:hover:text-stone-300">Blog</Link>
            <Link to="/trial/new" className="hover:text-stone-700 dark:hover:text-stone-300">Try free</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '';
  return (
    <li>
      <Link
        to={`/blog/${post.slug}`}
        className="block rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-sm transition-all p-5 h-full"
      >
        {post.coverImageUrl && (
          <img
            src={post.coverImageUrl}
            alt=""
            loading="lazy"
            className="w-full h-40 object-cover rounded-lg mb-3 bg-stone-100 dark:bg-stone-800"
          />
        )}
        <h2 className="text-lg font-semibold mb-1.5 leading-snug line-clamp-2">{post.title}</h2>
        {post.excerpt && (
          <p className="text-[13.5px] text-stone-600 dark:text-stone-400 mb-3 line-clamp-3">{post.excerpt}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <span>{date}</span>
          {post.tags.slice(0, 2).map((t) => (
            <span key={t} className="px-1.5 py-0.5 rounded bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-[10.5px] font-medium">
              {t}
            </span>
          ))}
        </div>
      </Link>
    </li>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  // Render up to 5 page buttons centred on the current page, with first/last
  // anchored. Smart-truncates with "…" when totalPages is large.
  const pages = useMemo<Array<number | '…'>>(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out: Array<number | '…'> = [1];
    const left = Math.max(2, page - 1);
    const right = Math.min(totalPages - 1, page + 1);
    if (left > 2) out.push('…');
    for (let i = left; i <= right; i++) out.push(i);
    if (right < totalPages - 1) out.push('…');
    out.push(totalPages);
    return out;
  }, [page, totalPages]);

  return (
    <nav className="mt-10 flex flex-col items-center gap-3" aria-label="Pagination">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-sm hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Prev
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-sm text-stone-400">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'inline-flex items-center justify-center h-9 min-w-9 px-2.5 rounded-lg text-sm font-medium tabular-nums',
                p === page
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:border-brand-300',
              )}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-sm hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="text-xs text-stone-500 tabular-nums">
        Showing {rangeStart}–{rangeEnd} of {total} articles
      </p>
    </nav>
  );
}
